import axios from 'axios';
import { getTokens, saveTokens, clearTokens } from './authApi';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/admin/stats';

const statsApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Add authentication token to all requests
statsApi.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh and 401 errors
statsApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          // Try to refresh token
          const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`, {
            refreshToken: tokens.refreshToken,
          });

          const { tokens: newTokens } = response.data;
          saveTokens(newTokens);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return statsApi(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        clearTokens();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Dashboard Statistics Interfaces
export interface OwnerDashboardStats {
  totalUsers: number;
  activeEstimates: number;
  activeWorkOrders: number;
  revenueMTD: number;
  pendingApprovals: number;
  overdueWorkOrders: number;
  activeTechnicians: number;
  totalTechnicians: number;
  averageTicketValue: number;
  customerSatisfactionRating: number;
  revenueGrowth: number;
  carCountMTD: number;
  estimateCloseRate: number | null;
  woCountsByStatus: {
    new: number;
    scheduled: number;
    in_progress: number;
    waiting_parts: number;
    waiting_approval: number;
    completed: number;
  };
  recentUsers: Array<{
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
    status: string;
    last_login: string | null;
    created_at: string;
  }>;
}

export interface AdvisorDashboardStats {
  activeEstimates: number;
  monthlySales: number;
  customerContacts: number;
  customersThisMonth: number;
  recentEstimates: Array<{
    id: number;
    estimate_number: string;
    customer_name: string;
    vehicle: string;
    total_amount: number;
    status: string;
    created_at: string;
  }>;
  pendingFollowUps: number;
  salesGoal: number;
  salesProgress: number;
  estimatesCreated: number;
  estimatesGoal: number;
  conversionRate: number;
  commissionMonth: number;
  commissionQuarter: number;
  commissionYTD: number;
}

export interface TechnicianDashboardStats {
  assignedWorkOrders: number;
  workOrdersInProgress: number;
  completedToday: number;
  hoursLoggedToday: number;
  myWorkOrders: Array<{
    id: number;
    wo_number: string;
    customer_name: string;
    vehicle: string;
    description: string;
    status: string;
    priority: string;
    estimated_completion: string | null;
  }>;
  todaysSchedule: Array<{
    id: number;
    time: string;
    customer_name: string;
    vehicle: string;
    service_type: string;
    status: string;
  }>;
  hoursLoggedWeek: number;
  workOrdersCompletedWeek: number;
  averageCompletionTime: number;
  efficiencyRating: number;
  clockInTime: string | null;
  clockOutTime: string | null;
}

export interface CustomerPortalStats {
  pendingEstimates: number;
  myVehicles: Array<{
    id: number;
    year: number;
    make: string;
    model: string;
    vin: string;
    license_plate: string;
    current_mileage: number | null;
  }>;
  serviceHistory: Array<{
    id: number;
    date: string;
    service_type: string;
    description: string;
    total_amount: number;
    status: string;
  }>;
  totalSpentYTD: number;
  upcomingReminders: Array<{
    id: number;
    vehicle: string;
    service_type: string;
    due_date: string;
    due_mileage: number | null;
  }>;
}

// API Functions
export const getOwnerDashboardStats = async (): Promise<OwnerDashboardStats> => {
  const response = await statsApi.get<OwnerDashboardStats>('/owner-dashboard');
  return response.data;
};

export const getAdvisorDashboardStats = async (): Promise<AdvisorDashboardStats> => {
  const response = await statsApi.get<AdvisorDashboardStats>('/advisor-dashboard');
  return response.data;
};

export const getTechnicianDashboardStats = async (): Promise<TechnicianDashboardStats> => {
  const response = await statsApi.get<TechnicianDashboardStats>('/technician-dashboard');
  return response.data;
};

export const getCustomerPortalStats = async (): Promise<CustomerPortalStats> => {
  const response = await statsApi.get<CustomerPortalStats>('/customer-dashboard');
  return response.data;
};
